import React, { createContext, useContext, useState, useEffect } from 'react';

// ============================================
// LAUNCH CONFIGURATION - Edit these values
// ============================================
const LAUNCH_CONFIG = {
  // Set to true when ready to go live on Amazon
  isLive: false,
  
  // Launch date (YYYY, MM-1, DD) - Month is 0-indexed (0=Jan, 7=Aug)
  // Example: August 20, 2026
  launchDate: new Date(2026, 7, 20),
  
  // Amazon product link (update when live)
  amazonLink: 'https://www.amazon.de',
};
// ============================================

const LanguageContext = createContext();

// Calculate days until launch
const getDaysUntilLaunch = () => {
  const now = new Date();
  const diffTime = LAUNCH_CONFIG.launchDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

const translations = {
  de: {
    nav: {
      story: 'Geschichte',
      product: 'Produkt',
      benefits: 'Wirkung',
      why: 'Warum Elemantra',
      buyOnAmazon: '78 Tage bis Start',
    },
    hero: {
      subtitle: 'Kaltgepresstes Rizinusöl',
      tagline: 'Die Kraft der Elemente.',
      cta: '78 Tage bis zum Start',
      ctaSubtext: 'Bald auf Amazon erhältlich',
    },
    story: {
      overline: 'Unsere Geschichte',
      title: 'Die Kraft der Elemente',
      p1: 'Seit Jahrtausenden kennen die Menschen die außergewöhnlichen Eigenschaften von Rizinusöl. Von den alten Ägyptern geschätzt, über Generationen weitergegeben — ein zeitloses Geheimnis der Natur.',
      p2: 'Elemantra ehrt diese Tradition. Wir bieten reine Einfachheit: Ein Inhaltsstoff, sorgfältig gewonnen, ohne Kompromisse. Keine Trends, keine leeren Versprechen — nur das Wesentliche.',
      p3: 'In einer Welt voller Komplexität bietet Elemantra Klarheit.',
    },
    benefits: {
      overline: 'Natürliche Wirkung',
      title: 'Die Kraft von Rizinusöl',
      subtitle: 'Reich an Rizinolsäure — einem einzigartigen Fettsäurekomplex, der seit Generationen für seine pflegenden Eigenschaften geschätzt wird.',
      item1: {
        title: 'Haut & Feuchtigkeit',
        desc: 'Die reichhaltige Textur pflegt die Haut tiefgehend und unterstützt die natürliche Hautbarriere.',
      },
      item2: {
        title: 'Haar & Kopfhaut',
        desc: 'Fördert die Durchblutung der Kopfhaut und nährt die Haarfollikel für kräftiges, glänzendes Haar.',
      },
      item3: {
        title: 'Natürliche Pflege',
        desc: 'Vitamin E und natürliche Fettsäuren unterstützen die Regeneration und Geschmeidigkeit.',
      },
      item4: {
        title: 'Vielseitig anwendbar',
        desc: 'Für Haut, Haare, Wimpern, Augenbrauen und Nägel — ein Öl für alle Bedürfnisse.',
      },
    },
    product: {
      overline: 'Das Produkt',
      title: 'Reine Eleganz',
      subtitle: '100% reines, kaltgepresstes Rizinusöl · 50ml',
      feature1: 'Kaltgepresst',
      feature2: '100% Rein',
      feature3: 'GMO-Frei',
    },
    why: {
      overline: 'Warum Elemantra',
      title: 'Kompromisslose Qualität',
      item1: {
        title: 'USDA Organic',
        desc: 'Zertifiziert biologisch nach höchsten Standards.',
      },
      item2: {
        title: 'Kaltgepresst',
        desc: 'Schonend gewonnen, um die natürliche Reinheit zu bewahren.',
      },
      item3: {
        title: '100% Rein',
        desc: 'Ein einziger Inhaltsstoff — nichts hinzugefügt, nichts weggenommen.',
      },
      item4: {
        title: 'GMO-Frei',
        desc: 'Natürlich und frei von gentechnisch veränderten Organismen.',
      },
    },
    purchase: {
      overline: 'Demnächst',
      title: 'Launch auf Amazon',
      subtitle: 'Noch 78 Tage bis zum Start',
      cta: '78 Tage',
      note: 'Bald auf Amazon.de erhältlich',
    },
    footer: {
      tagline: 'Die Kraft der Elemente.',
      rights: 'Alle Rechte vorbehalten.',
    },
  },
  en: {
    nav: {
      story: 'Story',
      product: 'Product',
      benefits: 'Benefits',
      why: 'Why Elemantra',
      buyOnAmazon: '78 Days to Launch',
    },
    hero: {
      subtitle: 'Cold-Pressed Castor Oil',
      tagline: 'The Power of the Elements.',
      cta: '78 Days to Launch',
      ctaSubtext: 'Coming soon on Amazon',
    },
    story: {
      overline: 'Our Story',
      title: 'The Power of the Elements',
      p1: 'For millennia, humanity has known the extraordinary properties of castor oil. Cherished by the ancient Egyptians, passed down through generations — a timeless secret of nature.',
      p2: 'Elemantra honors this tradition. We offer pure simplicity: One ingredient, carefully extracted, without compromise. No trends, no empty promises — only the essential.',
      p3: 'In a world of complexity, Elemantra offers clarity.',
    },
    benefits: {
      overline: 'Natural Benefits',
      title: 'The Power of Castor Oil',
      subtitle: 'Rich in ricinoleic acid — a unique fatty acid complex treasured for generations for its nourishing properties.',
      item1: {
        title: 'Skin & Moisture',
        desc: 'The rich texture deeply nourishes skin and supports the natural skin barrier.',
      },
      item2: {
        title: 'Hair & Scalp',
        desc: 'Promotes scalp circulation and nourishes hair follicles for strong, lustrous hair.',
      },
      item3: {
        title: 'Natural Care',
        desc: 'Vitamin E and natural fatty acids support regeneration and suppleness.',
      },
      item4: {
        title: 'Versatile Use',
        desc: 'For skin, hair, lashes, brows, and nails — one oil for all needs.',
      },
    },
    product: {
      overline: 'The Product',
      title: 'Pure Elegance',
      subtitle: '100% pure, cold-pressed castor oil · 50ml',
      feature1: 'Cold-Pressed',
      feature2: '100% Pure',
      feature3: 'GMO-Free',
    },
    why: {
      overline: 'Why Elemantra',
      title: 'Uncompromising Quality',
      item1: {
        title: 'USDA Organic',
        desc: 'Certified organic to the highest standards.',
      },
      item2: {
        title: 'Cold-Pressed',
        desc: 'Gently extracted to preserve natural purity.',
      },
      item3: {
        title: '100% Pure',
        desc: 'A single ingredient — nothing added, nothing removed.',
      },
      item4: {
        title: 'GMO-Free',
        desc: 'Natural and free from genetically modified organisms.',
      },
    },
    purchase: {
      overline: 'Coming Soon',
      title: 'Launching on Amazon',
      subtitle: '78 days until we go live',
      cta: '78 Days',
      note: 'Coming soon on Amazon.de',
    },
    footer: {
      tagline: 'The Power of the Elements.',
      rights: 'All rights reserved.',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [daysUntilLaunch, setDaysUntilLaunch] = useState(getDaysUntilLaunch());

  useEffect(() => {
    const savedLang = localStorage.getItem('elementa-lang');
    if (savedLang && (savedLang === 'de' || savedLang === 'en')) {
      setLanguage(savedLang);
    }
    
    // Update countdown daily
    const interval = setInterval(() => {
      setDaysUntilLaunch(getDaysUntilLaunch());
    }, 1000 * 60 * 60); // Update every hour
    
    return () => clearInterval(interval);
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'de' ? 'en' : 'de';
    setLanguage(newLang);
    localStorage.setItem('elementa-lang', newLang);
  };

  const t = translations[language];
  
  // Dynamic countdown text
  const countdown = {
    isLive: LAUNCH_CONFIG.isLive,
    amazonLink: LAUNCH_CONFIG.amazonLink,
    days: daysUntilLaunch,
    navText: LAUNCH_CONFIG.isLive 
      ? (language === 'de' ? 'Auf Amazon kaufen' : 'Buy on Amazon')
      : (language === 'de' ? `${daysUntilLaunch} Tage bis Start` : `${daysUntilLaunch} Days to Launch`),
    heroText: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Auf Amazon kaufen' : 'Buy on Amazon')
      : (language === 'de' ? `${daysUntilLaunch} Tage bis zum Start` : `${daysUntilLaunch} Days to Launch`),
    heroSubtext: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Jetzt auf Amazon erhältlich' : 'Now available on Amazon')
      : (language === 'de' ? 'Bald auf Amazon erhältlich' : 'Coming soon on Amazon'),
    purchaseTitle: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Jetzt auf Amazon' : 'Now on Amazon')
      : (language === 'de' ? 'Launch auf Amazon' : 'Launching on Amazon'),
    purchaseSubtitle: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Jetzt exklusiv auf Amazon Deutschland erhältlich' : 'Now available exclusively on Amazon Germany')
      : (language === 'de' ? `Noch ${daysUntilLaunch} Tage bis zum Start` : `${daysUntilLaunch} days until we go live`),
    purchaseCta: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Auf Amazon kaufen' : 'Buy on Amazon')
      : (language === 'de' ? `${daysUntilLaunch} Tage` : `${daysUntilLaunch} Days`),
    purchaseNote: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Verfügbar auf Amazon.de' : 'Available on Amazon.de')
      : (language === 'de' ? 'Bald auf Amazon.de erhältlich' : 'Coming soon on Amazon.de'),
    purchaseOverline: LAUNCH_CONFIG.isLive
      ? (language === 'de' ? 'Jetzt erhältlich' : 'Available Now')
      : (language === 'de' ? 'Demnächst' : 'Coming Soon'),
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t, countdown }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
